import { useEffect, useCallback, RefObject } from "react"
import MuxPlayerElement from "@mux/mux-player"

interface TrimData {
  start?: number
  end?: number
}

interface UseMuxTrimEnforcementOptions {
  playerRef: RefObject<MuxPlayerElement>
  trimData?: TrimData
  onLoadedMetadata?: () => void
  onTimeUpdate?: (currentTime: number, duration: number) => void
  onEnded?: () => void
}

/**
 * Custom hook that enforces trim boundaries on a MuxPlayer element
 * Handles setting initial position, enforcing boundaries during playback,
 * and clamping seek operations within trim bounds
 */
export const useMuxTrimEnforcement = ({
  playerRef,
  trimData,
  onLoadedMetadata,
  onTimeUpdate,
  onEnded
}: UseMuxTrimEnforcementOptions) => {

  const handleLoadedMetadata = useCallback(() => {
    const player = playerRef.current
    if (!player) return

    // Set initial position to trim start when video loads
    if (trimData?.start) {
      player.currentTime = trimData.start
    }

    onLoadedMetadata?.()
  }, [playerRef, trimData, onLoadedMetadata])

  const handleTimeUpdate = useCallback(() => {
    const player = playerRef.current
    if (!player) return

    const currentTime = player.currentTime
    const duration = player.duration

    // Call the optional onTimeUpdate callback
    onTimeUpdate?.(currentTime, duration)

    // Enforce trim end boundary during playback
    if (trimData && !player.paused) {
      const trimEnd = trimData.end ?? duration

      // Pause when reaching the trim end
      if (currentTime >= trimEnd - 0.05) {
        player.pause()
        const trimStart = trimData.start ?? 0
        player.currentTime = trimStart
        onEnded?.()
      }
    }
  }, [playerRef, trimData, onTimeUpdate, onEnded])

  const handlePlay = useCallback(() => {
    const player = playerRef.current
    if (!player || !trimData) return

    const currentTime = player.currentTime
    const trimEnd = trimData.end ?? player.duration
    const trimStart = trimData.start ?? 0

    // If playing from beyond trim end, reset to start
    if (currentTime >= trimEnd - 0.1) {
      player.currentTime = trimStart
    }
  }, [playerRef, trimData])

  const handleSeeked = useCallback(() => {
    const player = playerRef.current
    if (!player || !trimData) return

    const currentTime = player.currentTime
    const trimStart = trimData.start ?? 0
    const trimEnd = trimData.end ?? player.duration

    // Clamp seeking within trim boundaries
    if (currentTime < trimStart) {
      player.currentTime = trimStart
    } else if (currentTime > trimEnd) {
      player.currentTime = trimEnd
    }
  }, [playerRef, trimData])

  const handleEnded = useCallback(() => {
    onEnded?.()
  }, [onEnded])

  // Set up event listeners
  useEffect(() => {
    const player = playerRef.current
    if (!player) return

    player.addEventListener('loadedmetadata', handleLoadedMetadata)
    player.addEventListener('timeupdate', handleTimeUpdate)
    player.addEventListener('play', handlePlay)
    player.addEventListener('seeked', handleSeeked)
    player.addEventListener('ended', handleEnded)

    return () => {
      player.removeEventListener('loadedmetadata', handleLoadedMetadata)
      player.removeEventListener('timeupdate', handleTimeUpdate)
      player.removeEventListener('play', handlePlay)
      player.removeEventListener('seeked', handleSeeked)
      player.removeEventListener('ended', handleEnded)
    }
  }, [handleLoadedMetadata, handleTimeUpdate, handlePlay, handleSeeked, handleEnded, playerRef])

  return {
    handleLoadedMetadata,
    handleTimeUpdate,
    handlePlay,
    handleSeeked,
    handleEnded
  }
}

export default useMuxTrimEnforcement
